import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { CalendarDays, Car, CheckCircle2, Phone, User, Wrench, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";

const services = [
  "Service & Reparation",
  "Däckbyte & Däckhotell",
  "AC-Service",
  "Felsökning & Diagnostik",
  "Besiktningshjälp",
  "Bromsar & Avgassystem",
];

function formatRegnr(v) {
  const clean = v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  if (clean.length > 3) return `${clean.slice(0, 3)} ${clean.slice(3)}`;
  return clean;
}

function formatPhone(v) {
  return v.replace(/[^\d+\s]/g, "").slice(0, 16);
}

export default function Booking() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [regnr, setRegnr] = useState("");
  const [service, setService] = useState("");
  const [date, setDate] = useState(undefined);
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = name && phone && regnr && service && date;

  const dateLabel = useMemo(
    () => (date ? format(date, "EEEE d MMMM yyyy", { locale: sv }) : "Välj datum"),
    [date]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Fyll i alla fält för att skicka din bokningsförfrågan.");
      return;
    }
    setSubmitted(true);
    toast.success("Så här kan bokningsbekräftelsen se ut.", {
      description: `${service} • ${format(date, "d MMM yyyy", { locale: sv })} • ${regnr}`,
    });
  };

  const reset = () => {
    setSubmitted(false);
    setName("");
    setPhone("");
    setRegnr("");
    setService("");
    setDate(undefined);
  };

  return (
    <section id="boka" className="relative py-24 lg:py-36 bg-white overflow-hidden">
      {/* ambient blobs */}
      <div className="absolute -top-20 -left-20 h-[400px] w-[400px] rounded-full bg-[#D52027]/5 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -right-20 h-[500px] w-[500px] rounded-full bg-[#1F3C88]/8 blur-3xl pointer-events-none" />

      <div className="relative max-w-[1360px] mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-6 lg:items-end mb-14 lg:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <span className="eyebrow">03 — Boka tid</span>
            <h2 className="hero-text mt-5 text-[9vw] sm:text-[6vw] lg:text-[4.4vw] xl:text-[72px]">
              Berätta vad bilen behöver.<br />
              <span className="italic text-[#1F3C88]">Vi tar det därifrån.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 text-slate-600 text-[1.02rem] leading-relaxed max-w-md"
          >
            Fyll i det viktigaste om bilen och vad du vill ha hjälp med. Därefter
            kan ZAM återkomma med tid, upplägg och en tydlig prisbild.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9 }}
          className="relative rounded-[28px] border border-slate-200 bg-white shadow-[0_40px_80px_-40px_rgba(15,23,42,0.25)] overflow-hidden"
        >
          <div className="grid lg:grid-cols-5">
            {/* LEFT – form */}
            <div className="lg:col-span-3 p-7 lg:p-12 border-b lg:border-b-0 lg:border-r border-slate-200">
              <div className="flex items-center justify-between">
                <div className="ticker">Bokningsformulär</div>
                <div className="flex items-center gap-1.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <span
                      key={i}
                      className={`h-1.5 w-6 rounded-full transition-colors ${
                        [name, phone, regnr, service, date].filter(Boolean).length >=
                        i
                          ? "bg-[#D52027]"
                          : "bg-slate-200"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {!submitted ? (
                <form onSubmit={handleSubmit} className="mt-8 grid gap-5">
                  <Field label="Namn" icon={User}>
                    <Input
                      data-testid="booking-form-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="För- och efternamn"
                      className="zam-input"
                    />
                  </Field>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Telefonnummer" icon={Phone}>
                      <Input
                        data-testid="booking-form-phone-input"
                        value={phone}
                        onChange={(e) => setPhone(formatPhone(e.target.value))}
                        placeholder="070 123 45 67"
                        inputMode="tel"
                        className="zam-input"
                      />
                    </Field>

                    <Field label="Registreringsnummer" icon={Car}>
                      <Input
                        data-testid="booking-form-regnr-input"
                        value={regnr}
                        onChange={(e) => setRegnr(formatRegnr(e.target.value))}
                        placeholder="ABC 123"
                        className="zam-input uppercase tracking-widest"
                      />
                    </Field>
                  </div>

                  <Field label="Typ av tjänst" icon={Wrench}>
                    <Select value={service} onValueChange={setService}>
                      <SelectTrigger
                        data-testid="booking-form-service-select"
                        className="zam-input h-12 text-left"
                      >
                        <SelectValue placeholder="Välj tjänst" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Önskat datum" icon={CalendarDays}>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="booking-form-date-picker"
                          className="zam-input h-12 w-full flex items-center justify-between text-left px-3 text-slate-900"
                        >
                          <span className={date ? "" : "text-slate-400"}>
                            {dateLabel}
                          </span>
                          <CalendarDays className="h-4 w-4 text-slate-400" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="p-0 w-auto">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          locale={sv}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </Field>

                  <button
                    type="submit"
                    data-testid="booking-form-submit-button"
                    disabled={!canSubmit}
                    className={`mt-3 inline-flex items-center justify-center gap-2 h-14 rounded-full font-semibold text-[0.98rem] transition-all ${
                      canSubmit
                        ? "bg-[#D52027] text-white hover:bg-[#B81920] shadow-[0_18px_40px_-14px_rgba(213,32,39,0.55)]"
                        : "bg-slate-100 text-slate-400 cursor-not-allowed"
                    }`}
                  >
                    Skicka bokningsförfrågan
                    <ArrowUpRight className="h-5 w-5" />
                  </button>
                  <p className="text-[0.74rem] text-slate-500">
                    Demoläge — formuläret är ännu inte anslutet.
                  </p>
                </form>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="mt-8"
                >
                  <div className="h-14 w-14 rounded-full bg-emerald-100 grid place-items-center">
                    <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                  </div>
                  <h3 className="mt-6 font-display text-[2rem] text-slate-900 leading-tight">
                    Tack, {name.split(" ")[0]}!
                  </h3>
                  <p className="mt-3 text-slate-600 max-w-md">
                    Din förfrågan gäller <strong>{service}</strong> den{" "}
                    <strong>
                      {format(date, "d MMMM yyyy", { locale: sv })}
                    </strong>
                    . I den färdiga lösningen kan ZAM återkomma till {phone} med
                    bekräftad tid och nästa steg.
                  </p>
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="booking-form-reset"
                    className="mt-6 btn-ghost"
                  >
                    Boka en till tid
                  </button>
                </motion.div>
              )}
            </div>

            {/* RIGHT – summary */}
            <div className="lg:col-span-2 bg-slate-900 text-white p-7 lg:p-10 relative overflow-hidden">
              <div className="absolute inset-0 opacity-30 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at 20% 10%, rgba(213,32,39,0.35), transparent 50%), radial-gradient(circle at 90% 90%, rgba(31,60,136,0.5), transparent 50%)",
                }}
              />
              <div className="relative">
                <div className="ticker text-slate-400">Din bokning</div>
                <h3 className="mt-4 font-display text-[2rem] lg:text-[2.4rem] leading-[1.05]">
                  Översikt
                </h3>

                <div className="mt-8 grid gap-5">
                  <SummaryRow label="Namn" value={name || "—"} />
                  <SummaryRow label="Telefon" value={phone || "—"} />
                  <SummaryRow
                    label="Regnr."
                    value={regnr || "—"}
                    mono
                  />
                  <SummaryRow label="Tjänst" value={service || "—"} />
                  <SummaryRow
                    label="Datum"
                    value={date ? format(date, "d MMM yyyy", { locale: sv }) : "—"}
                  />
                </div>

                <div className="mt-10 pt-6 border-t border-white/10">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
                    <p className="text-[0.88rem] text-slate-300 leading-relaxed">
                      Tanken är enkel: tydlig avstämning före arbetet och kontakt om
                      något förändras på vägen.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <style>{`
        .zam-input {
          height: 48px;
          border-radius: 12px;
          background: #F8FAFC;
          border: 1px solid #E2E8F0;
          transition: all 0.25s ease;
        }
        .zam-input:focus-visible,
        .zam-input:focus,
        .zam-input:focus-within {
          background: #ffffff;
          border-color: #1F3C88;
          box-shadow: 0 0 0 4px rgba(31,60,136,0.08);
          outline: none;
        }
      `}</style>
    </section>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="flex items-center gap-2 text-[0.75rem] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
        {Icon && <Icon className="h-3.5 w-3.5 text-[#1F3C88]" strokeWidth={2} />}
        {label}
      </span>
      {children}
    </label>
  );
}

function SummaryRow({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-4 pb-4 border-b border-white/10">
      <span className="text-[0.75rem] uppercase tracking-[0.18em] font-semibold text-slate-400">
        {label}
      </span>
      <span
        className={`text-right text-white font-medium ${
          mono ? "font-mono tracking-widest" : "font-display text-[1.05rem]"
        } ${value === "—" ? "opacity-40" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}
